import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  UpdateWarehouseShelvingMutation,
  WarehouseShelvingMutationUpdateArgs,
  WarehouseShelvingUpdateInput,
} from "@/graphql/codegen/graphql"
import { UpdateWarehouseShelvingDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateWarehouseShelvingReturn {
  update: (input: WarehouseShelvingUpdateInput) => Promise<void>
  isUpdating: boolean
  error?: CombinedError
}

export const useUpdateWarehouseShelving = (onSuccess: () => void): UseUpdateWarehouseShelvingReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    UpdateWarehouseShelvingMutation,
    WarehouseShelvingMutationUpdateArgs
  >(UpdateWarehouseShelvingDocument)

  const update = async (input: WarehouseShelvingUpdateInput) => {
    const { data, error } = await executeMutation({ input })
    const hasError = error || data?.warehouseShelving.update.__typename === "WarehouseShelvingUpdateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        message: error?.message ?? "Failed to update warehouse shelving.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      content: "Warehouse shelving successfully updated.",
    })
    onSuccess()
  }

  return {
    update,
    isUpdating: fetching,
    error,
  }
}
